"use client";

import { useState, useTransition } from "react";
import Link from "next/link";
import { Edit3, ExternalLink, X } from "lucide-react";
import { quickUpdateArticle } from "@/lib/admin-actions";

type EditableArticle = {
  id: string;
  title: string;
  excerpt: string;
  coverImage: string;
  status: string;
};

export function ArticleEditorToolbar({ article }: { article: EditableArticle }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  return (
    <>
      <div className="mb-5 flex max-w-full flex-wrap items-center gap-2 border-l-4 border-[#e5481d] bg-white px-3 py-3 text-zinc-950">
        <span className="text-xs font-black uppercase text-zinc-500">Szerkesztői mód</span>
        <Link
          href={`/admin/articles/${article.id}/edit`}
          className="inline-flex min-h-9 items-center gap-2 rounded-lg bg-[#111] px-3 py-2 text-xs font-black uppercase text-white hover:bg-[#e5481d] sm:text-sm"
        >
          <ExternalLink size={15} aria-hidden="true" />
          Cikk szerkesztése
        </Link>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex min-h-9 items-center gap-2 rounded-lg border border-zinc-300 px-3 py-2 text-xs font-black uppercase text-zinc-800 hover:border-[#e5481d] hover:text-[#e5481d] sm:text-sm"
        >
          <Edit3 size={15} aria-hidden="true" />
          Gyors szerkesztés
        </button>
      </div>

      {open ? (
        <div className="fixed inset-0 z-50 overflow-hidden bg-black/45 p-2 sm:p-4" role="dialog" aria-modal="true">
          <div className="ml-auto flex h-full w-full max-w-lg flex-col rounded-lg bg-white text-zinc-950 shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-zinc-200 p-4">
              <h2 className="text-lg font-black uppercase sm:text-xl">Gyors szerkesztés</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="grid h-9 w-9 place-items-center rounded-lg border border-zinc-200 hover:bg-zinc-100"
                aria-label="Bezárás"
              >
                <X size={17} aria-hidden="true" />
              </button>
            </div>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                setError(null);
                startTransition(async () => {
                  const result = await quickUpdateArticle(formData);
                  if (!result.ok) {
                    setError(result.message);
                    return;
                  }
                  setOpen(false);
                });
              }}
              className="grid min-h-0 gap-4 overflow-y-auto p-4"
            >
              <input type="hidden" name="articleId" value={article.id} />
              <label className="grid gap-1 text-sm font-bold">
                Cím
                <input
                  name="title"
                  defaultValue={article.title}
                  className="h-11 rounded-lg border border-zinc-300 px-3 outline-none focus:border-[#e5481d]"
                />
              </label>
              <label className="grid gap-1 text-sm font-bold">
                Lead
                <textarea
                  name="excerpt"
                  defaultValue={article.excerpt}
                  rows={4}
                  className="rounded-lg border border-zinc-300 p-3 outline-none focus:border-[#e5481d]"
                />
              </label>
              <label className="grid gap-1 text-sm font-bold">
                Borítókép URL
                <input
                  name="coverImage"
                  defaultValue={article.coverImage}
                  className="h-11 rounded-lg border border-zinc-300 px-3 outline-none focus:border-[#e5481d]"
                />
              </label>
              <label className="grid gap-1 text-sm font-bold">
                Státusz
                <select
                  name="status"
                  defaultValue={article.status}
                  className="h-11 rounded-lg border border-zinc-300 bg-white px-3 outline-none focus:border-[#e5481d]"
                >
                  <option value="PUBLISHED">PUBLISHED</option>
                  <option value="DRAFT">DRAFT</option>
                </select>
              </label>
              <div className="flex flex-wrap justify-end gap-2 border-t border-zinc-200 pt-4">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded-lg border border-zinc-300 px-4 py-2 text-sm font-black uppercase"
                >
                  Mégse
                </button>
                <button disabled={pending} className="rounded-lg bg-[#e5481d] px-4 py-2 text-sm font-black uppercase text-white disabled:cursor-wait disabled:opacity-60">
                  {pending ? "Mentés..." : "Mentés"}
                </button>
              </div>
              {error ? <p className="text-sm font-semibold text-[#A4243B]">{error}</p> : null}
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
